/*
<summary>
   This functional component is all about rendering the Crimping Inspection Record list/grid.
   Developer: Aashish Singh, Created Date: 24-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import CardWrapper from '../../shared-components/card-wrapper/card-wrapper';
import Header from '../../shared-components/header/header';
import Grid from '../../shared-components/Grid/Grid';
import Pagination from '../../shared-components/Grid/Pagination';
import InspectionFilter from '../../shared-components/inspection-filter/inspection-filter';
import { RefreshIcon, ExportIcon } from '../../shared-components/svgs/svgs';
import { formatMessage } from '../../translations/format-message';
import './crimping-inspection-record.css';
import userSettingEnum from '../../constants/enums/user-setting-enum';
import { useStore } from '../../contexts/store-provider';
import { ISharedInspectionFilter, SharedInspectionFilterInitialState } from '../../models/ISharedInspectionFilter';
import { getTenantAccessFromLocalStorage } from '../../helpers/localstorage-helper';
import IDefaultColumSortOrder from '../../models/IDefaultColumSortOrder';
import { CrimpingInspectionRecordDefaultSortColumnOrder } from '../../core/initialState/filter-initial-state';
import { formatGridDateTimeWithSeconds } from '../../helpers/date-time-helper';
import { emptyValue } from '../../constants/constants';
import IPageInfo, { IPageLimitInfo } from '../../models/ICommon';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import { GridRemoteConstant } from '../../constants/grid-constant';
import noMessageIcon from '../../assets/Images/filterNodata.png';

interface IProps {
  embedded?: boolean;
  // When embedded, the host page (Equipment Inspection Menu) owns the filter bar and
  // refresh/export actions - it passes the current filter down, and bumps filterVersion to
  // signal "re-fetch with this filter" (e.g. Search/Clear/Refresh was clicked there).
  sharedFilter?: ISharedInspectionFilter;
  filterVersion?: number;
}

/**
* This functional component is used to render the Crimping Inspection Record list grid. This
* is a read-only list — there is no add/edit/delete for inspection records.
* @param props: embedded - true when rendered inside the Equipment Inspection Menu tabs;
* sharedFilter/filterVersion - filter state owned by the host page when embedded
*/
const CrimpingInspectionRecord = ({ embedded, sharedFilter, filterVersion }: IProps) => {
  // When embedded, the fixed-position top toolbar this component uses as a standalone routed
  // page would overlap the app's own top navbar instead of flowing under the tab bar - and
  // the filter/refresh/export UI is owned by the host page instead, so none of it renders.
  const isEmbedded = !!embedded;
  const { preferencesStore, crimpingInspectionRecordStore, factoryStore } = useStore();
  const { userSetting, setUserSetting, islanguageChanged, updateLanguageChangedFlag } =
    preferencesStore;
  const {
    toolCrimpInspectionList,
    getToolCrimpInspectionList,
    GetToolCrimpInspectionListService,
    getAllToolCrimpInspectionState,
    ExportCrimpingInspectionRecordFileService,
  } = crimpingInspectionRecordStore;
  const { allAvailableFactories, GetAllFactoriesService } = factoryStore;
  const isFirstFilterVersion = useRef(true);

  const [filterObj, setFilterObj] = useState<ISharedInspectionFilter>(() => {
    const accessedFactoryId = getTenantAccessFromLocalStorage();
    return accessedFactoryId !== -1
      ? { ...SharedInspectionFilterInitialState, OwnerFactoryId: accessedFactoryId }
      : SharedInspectionFilterInitialState;
  });
  const [columSortOrder] = useState<IDefaultColumSortOrder>(CrimpingInspectionRecordDefaultSortColumnOrder);
  const [isFilterCleared, setIsFilterCleared] = useState(true);

  const pageLimitOptions: Array<number> = [10, 20, 50, 100];
  const [disablePagination, setDisablePagination] = useState<boolean>(false);

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const activeFilter = isEmbedded ? sharedFilter || SharedInspectionFilterInitialState : filterObj;

  /**
  * This function is used to call the crimp tool inspection list API with the current filter
  * and paging state.
  * @param filter: Filter criteria to apply to the list request
  * @param filterApplied: Flag indicating a filter was just applied, so the page is reset to 1
  */
  const getListServiceHandler = (
    filter?: ISharedInspectionFilter,
    filterApplied?: boolean
  ) => {
    GetToolCrimpInspectionListService(
      filterApplied ? 1 : pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      filter ?? activeFilter
    );
  };

  /**
   * This useEffect is used to load the owner factory list (needed for the filter dropdown
   * when this page owns its own filter, i.e. not embedded) and the inspection record list on
   * initial mount.
   */
  useEffect(() => {
    if (!isEmbedded) GetAllFactoriesService(true);
    getListServiceHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This useEffect re-fetches with the host page's filter whenever it changes (embedded mode
   * only) - filterVersion increments on Search/Clear/Refresh there.
   */
  useEffect(() => {
    if (!isEmbedded) return;
    if (isFirstFilterVersion.current) {
      isFirstFilterVersion.current = false;
      return;
    }
    getListServiceHandler(sharedFilter, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVersion]);

  /**
   * This useEffect is used to sync the current page and total pages with the latest list
   * response.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      currentPage: toolCrimpInspectionList?.PagingDetails?.CurrentPage,
      totalPages: toolCrimpInspectionList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolCrimpInspectionList]);

  /**
   * This useEffect is used to reload the list whenever the current page changes via the pager.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      getListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * This useEffect is used to reload the list whenever the page size selection changes.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      getListServiceHandler();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  /**
   * This useEffect is used to reload the list and reset the language changed flag whenever the
   * selected language changes.
   */
  useEffect(() => {
    if (islanguageChanged) {
      getListServiceHandler();
      updateLanguageChangedFlag();
    }
  }, [islanguageChanged]);

  const handleRefresh = () => {
    getListServiceHandler();
  };

  const handleExportClick = () => {
    ExportCrimpingInspectionRecordFileService(filterObj);
  };

  /**
  * This function is used to apply the filter values and reload the list.
  * @param object: Filter values submitted from the filter form
  */
  const filterHandler = (object: ISharedInspectionFilter) => {
    getListServiceHandler(object, true);
    setFilterObj({ ...object });
    setIsFilterCleared(false);
  };

  /**
  * This function is used to update the filter values, and reload the list when the filter is
  * cleared.
  * @param obj: Filter values
  * @param isfilterCleard: Flag indicating the filter was cleared
  */
  const filterParams = (obj: ISharedInspectionFilter, isfilterCleard: boolean) => {
    if (isfilterCleard) {
      setFilterObj({ ...obj });
      getListServiceHandler(obj, true);
      setIsFilterCleared(true);
    } else {
      setFilterObj({ ...obj });
    }
  };

  const onPageLimitChanged = (evt: any) => {
    setPageInfo({ ...pageInfo, currentPage: 1, isPagerChange: false });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const dateTimeFormatter = formatGridDateTimeWithSeconds;

  /**
  * This function is used as a column's `title` — it shows the full cell value as a native
  * tooltip only 
  * @param cell: Raw cell value
  */
  const showTitleTooltip = (cell: unknown): string | undefined => {
    if (cell === null || cell === undefined) return undefined;
    const text = String(cell);
    return text;
  };

  /**
  * This function returns a colored OK/NG badge for the calculated result cell.
  * @param cell: CalculatedResult value from the API - 1 = OK, 0 = NG, null/undefined = pending
  */
  const judgmentBadgeFormatter = (cell?: number | null) => {
    if (cell === null || cell === undefined) return '-';
    const isOk = Number(cell) === 1;
    return (
      <span className={`cir-badge ${isOk ? 'cir-badge-ok' : 'cir-badge-ng'}`}>
        {isOk ? 'OK' : 'NG'}
      </span>
    );
  };

  const columns: any = [
    {
      dataField: 'serialNumber',
      text: formatMessage('serial_number'),
      classes: 'show-Elipses',
      isDummyField: true,
      formatter: (_: any, __: any, rowIndex: number) => rowIndex + 1,
      sort: false,
    },
    {
      dataField: 'ToolManagementNo',
      text: formatMessage('tool_management_no'),
      classes: 'show-Elipses',
      sort: false,
      title: showTitleTooltip,
    },
    {
      dataField: 'ToolName',
      text: formatMessage('tool_name'),
      classes: 'show-Elipses',
      sort: false,
      title: showTitleTooltip,
    },
    {
      dataField: 'StartDate',
      text: formatMessage('start_date'),
      classes: 'show-Elipses',
      formatter: dateTimeFormatter,
      sort: false,
      title: (cell: string) => dateTimeFormatter(cell),
    },
    {
      dataField: 'EndDate',
      text: formatMessage('end_date'),
      classes: 'show-Elipses',
      formatter: dateTimeFormatter,
      sort: false,
      title: (cell: string) => dateTimeFormatter(cell),
    },
    {
      dataField: 'Duration',
      text: formatMessage('duration_second'),
      classes: 'show-Elipses',
      sort: false,
      formatter: (cell: number | null) => (cell === null || cell === undefined ? emptyValue : `${cell}s`),
      title: (cell: number | null) => (cell === null || cell === undefined ? '' : `${cell}s`),
    },
    {
      dataField: 'Awg',
      text: formatMessage('awg'),
      classes: 'show-Elipses',
      sort: false,
      title: showTitleTooltip,
    },
    {
      dataField: 'UpperLimit',
      text: formatMessage('upper_limit'),
      classes: 'show-Elipses',
      sort: false,
      title: showTitleTooltip,
    },
    {
      dataField: 'LowerLimit',
      text: formatMessage('lower_limit'),
      classes: 'show-Elipses',
      sort: false,
      title: showTitleTooltip,
    },
    {
      dataField: 'TensileStrength',
      text: formatMessage('tensile_strength_n'),
      classes: 'show-Elipses',
      sort: false,
      title: showTitleTooltip,
    },
    {
      dataField: 'CalculatedResult',
      text: formatMessage('calculated_result'),
      classes: 'show-Elipses',
      formatter: judgmentBadgeFormatter,
      sort: false,
    },
    {
      dataField: 'NgReason',
      text: formatMessage('ng_reason'),
      classes: 'show-Elipses',
      sort: false,
      title: showTitleTooltip,
    },
  ];

  return (
    <>
      {!isEmbedded && (
        <>
          <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
            <div className="row align-items-center">
              <Header
                className="col"
                label="crimping_inspection_record_list"
              />
              <div className="col-lg-7 col-md-6 col-sm-6">
                <ul className="add-list">
                  <li title={formatMessage('refresh')}>
                    <a
                      className="btn btn-light"
                      onClick={handleRefresh}
                    >
                      <RefreshIcon />
                    </a>
                  </li>
                  <li title={formatMessage('export_csv')}>
                    <a
                      className="btn btn-secondary"
                      onClick={handleExportClick}
                    >
                      <span className="me-1">
                        <ExportIcon />
                      </span>
                      <FormattedMessage id="export_csv" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
      {/* When embedded, the host page (Equipment Inspection Menu) already wraps the filter
          and this grid together in one shared card - wrapping again here would show as a
          visible seam (two stacked white boxes each with their own shadow/rounded corners)
          instead of one continuous page. */}
      <CardWrapper embedded={isEmbedded}>
        {!isEmbedded && (
          <InspectionFilter
            initialValues={filterObj}
            submitHandler={filterHandler}
            filterParmas={filterParams}
            isLoading={false}
            isFilterCleared={isFilterCleared}
            factoryOptions={allAvailableFactories}
          />
        )}
        {getAllToolCrimpInspectionState.inProgress && (
          <FormLoader loading={getAllToolCrimpInspectionState.inProgress} />
        )}
        {getToolCrimpInspectionList && getToolCrimpInspectionList.length > 0 ? (
          <>
            <div
              className={`tableDesign row-eq-height tableDevices p-3 changingPanel ${
                isEmbedded ? 'eim-inspection-table' : ''
              }`}
            >
              <div className="table-responsive">
                <Grid
                  data={getToolCrimpInspectionList}
                  columns={columns}
                  keyField="Id"
                  loading={getAllToolCrimpInspectionState.inProgress}
                  hover={true}
                  sort={{ ...columSortOrder }}
                  remote={{ ...GridRemoteConstant }}
                />
              </div>
            </div>
            <div className="row no-gutters p-3 pt-0 tableFooter">
              <div className="col-lg-3 col-md-6 col-sm-6 pt-1">
                <FormattedMessage id="show" />
                <select
                  className="pageLimit"
                  onChange={onPageLimitChanged}
                  value={pageLimitInfo.pageLimit}
                >
                  {pageLimitOptions.map((op: any) => (
                    <option
                      key={op}
                      value={op}
                    >
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-9 col-md-6 col-sm-6 d-flex justify-content-sm-end">
                <Pagination
                  isRefreshPage={false}
                  totalPages={pageInfo.totalPages}
                  pageLimit={pageLimitInfo.pageLimit}
                  onPageChanged={onPageChanged}
                  disablePagination={disablePagination}
                  setIsRefreshPage={setIsRefreshPage}
                  currentPage={pageInfo.currentPage}
                  totalRecords={toolCrimpInspectionList?.PagingDetails?.TotalRecords || 0}
                />
              </div>
            </div>
          </>
        ) : (
          !getAllToolCrimpInspectionState.inProgress && (
            <div className="message">
              <div className="d-flex message-area">
                <div className="no-data-img">
                  <img
                    src={noMessageIcon}
                    alt=""
                    className="no_message"
                  />
                </div>
                <div>
                  <h3 className="fw-bold display-6">
                    <FormattedMessage id="no_record" />
                  </h3>
                </div>
              </div>
            </div>
          )
        )}
      </CardWrapper>
    </>
  );
};

export default observer(CrimpingInspectionRecord);
